import { useMemo } from 'react';
import { ShieldCheck } from 'lucide-react';
import { Section } from '@vnetwork/ui/layout';
import { DataTable, EmptyState, StatusPill, type ColumnDef } from '@vnetwork/ui/data';
import { Button, Skeleton } from '@vnetwork/ui/primitives';
import styles from './AdvancedSiteList.module.css';

/** A per-website rule row shared by the per-site Advanced-Protection tabs (Page
 *  Defacement, CSRF, …): the website + whether the feature is enabled on it. */
export interface SiteStatusRow {
  site_id: number;
  domain: string;
  enabled: boolean;
}

/**
 * Shared "Websites" list for the per-website Advanced-Protection tabs that store
 * their rule inside the site config (Page Defacement, CSRF, …). Renders the
 * Website / Status / Edit table; the owning tab supplies its data + edit handler.
 */
export function AdvancedSiteList({
  title,
  description,
  sites,
  isLoading,
  onEdit,
}: {
  title: string;
  description: string;
  sites: SiteStatusRow[];
  isLoading: boolean;
  onEdit: (siteId: number) => void;
}) {
  const columns = useMemo<ColumnDef<SiteStatusRow>[]>(
    () => [
      {
        accessorKey: 'domain',
        header: 'Website',
        cell: ({ row }) => <span className={styles.name}>{row.original.domain}</span>,
      },
      {
        id: 'status',
        header: 'Status',
        cell: ({ row }) =>
          row.original.enabled ? (
            <StatusPill tone="success">Enabled</StatusPill>
          ) : (
            <StatusPill tone="neutral">Disabled</StatusPill>
          ),
      },
      {
        id: 'actions',
        header: 'Operation',
        cell: ({ row }) => (
          <Button variant="ghost" size="sm" onClick={() => onEdit(row.original.site_id)}>
            Edit
          </Button>
        ),
      },
    ],
    [onEdit],
  );

  return (
    <Section title={title} description={description}>
      {isLoading ? (
        <div className={styles.skeletonList}>
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className={styles.skelRow} />
          ))}
        </div>
      ) : (
        <DataTable
          columns={columns}
          data={sites}
          emptyState={
            <EmptyState
              size="sm"
              icon={<ShieldCheck size={20} />}
              title="No websites"
              description="No websites are available for this protection module."
            />
          }
        />
      )}
    </Section>
  );
}
